import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ImprovedVersionCard } from "@/components/results/ImprovedVersionCard";
import { PromptResponsePanel } from "@/components/results/PromptResponsePanel";
import { PromptJourneyRail } from "@/components/results/PromptJourneyRail";
import { PromptScoreSummaryBar } from "@/components/results/PromptScoreSummaryBar";
import { PromptModelPicker } from "@/components/results/PromptModelPicker";
import { FileText, Pencil, Check, X, ArrowRight } from "lucide-react";
import { DIMENSIONS } from "@/lib/rubricParser";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { ToolRecommendation } from "@/components/prompt-builder/ToolRecommendationCards";
import type { PromptResponseIteration } from "@/types/promptResponse";
import { derivePromptJourneyReceipt, type JourneyConversation } from "@/lib/promptJourneyReceipt";

// Dimension scores can be either a number or an object with score/rationale
type DimensionScoreValue = number | {
  score: number;
  rationale?: string;
};
interface AnalysisResult {
  ui: {
    input_kind: string;
    detected_type: string;
    why_type: string;
    original_text: string;
    whats_working: string[];
    improvements: string[];
    rewrite_tagged: string;
    rewrite_clean: string;
    bubbles: Array<{
      id: string;
      rubric_driver: string;
      title: string;
      body: string;
      anchor_hint: string;
    }>;
    quick_wins: string[];
    progress_points: {
      submission_points: number;
      bonus_points: number;
      total_points: number;
      bonus_explainer: string;
      suggested_next_move: string;
    };
    dimension_scores?: Record<string, DimensionScoreValue>;
    top_upgrades?: string[];
    prompt_summary?: string;
    rewrite_summary?: string;
    prompt_title?: string;
    score_id?: string;
    submission_id?: string;
    is_public?: boolean;
    original_score?: number;
    improved_score?: number;
    is_remix?: boolean;
    original_score_id?: string;
    source_original_prompt?: string;
    user_id?: string;
  };
  telemetry: {
    tags: string[];
  };
}
interface AnalysisResultsProps {
  result: AnalysisResult;
  onAnalyze?: (prompt: string) => Promise<void>;
  isLoading?: boolean;
  fromOnboarding?: boolean;
  toolRecommendations?: ToolRecommendation[] | null;
  recommendationId?: string | null;
  originalPrompt?: string | null;
  initialSelectedTool?: string | null;
}
const AnalysisResults = ({
  result,
  fromOnboarding,
  toolRecommendations,
  recommendationId,
  originalPrompt,
  initialSelectedTool,
}: AnalysisResultsProps) => {
  const navigate = useNavigate();
  const { ui } = result;
  const isMobile = useIsMobile();
  const { user, membership } = useAuth();
  const isChat = ui.input_kind === 'chat_string' || ui.input_kind === 'chat_full';

  // State for dynamic re-scoring
  const [currentScore, setCurrentScore] = useState<number | undefined>();

  // Lifted state for editable prompts
  const [currentRewriteClean, setCurrentRewriteClean] = useState(ui.rewrite_clean);
  const [currentRewriteTagged, setCurrentRewriteTagged] = useState(ui.rewrite_tagged);

  // Tool optimization state — initialize from builder selection if available
  const [selectedToolName, setSelectedToolName] = useState<string | null>(initialSelectedTool || null);
  const baseRewriteClean = useRef(ui.rewrite_clean);
  const [responseIterations, setResponseIterations] = useState<PromptResponseIteration[]>([]);
  const responseStepMock = import.meta.env.VITE_RESPONSE_STEP_MOCK === 'true';
  const [journeyConversation, setJourneyConversation] = useState<JourneyConversation | null>(null);
  const [isRemixReceipt, setIsRemixReceipt] = useState(Boolean(ui.is_remix));
  const [remixOriginalPrompt, setRemixOriginalPrompt] = useState<string | null>(ui.source_original_prompt || null);

  // Sync when initialSelectedTool arrives async (e.g. DB load on page refresh)
  useEffect(() => {
    if (initialSelectedTool && !selectedToolName) {
      setSelectedToolName(initialSelectedTool);
    }
  }, [initialSelectedTool, selectedToolName]);

  // State for editable title
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [promptTitle, setPromptTitle] = useState(ui.prompt_title || `${ui.detected_type} Prompt`);
  const [editingTitle, setEditingTitle] = useState(promptTitle);
  const [isSavingTitle, setIsSavingTitle] = useState(false);

  // Check if current user owns this prompt
  const isOwner = user?.id && ui.user_id ? user.id === ui.user_id : !!ui.score_id;

  // Bottom banner for post-onboarding
  const [showBottomBanner, setShowBottomBanner] = useState(!!fromOnboarding);

  useEffect(() => {
    if (!ui.score_id) return;
    if (responseStepMock) return;

    const loadResponseIterations = async () => {
      const { data, error } = await supabase
        .from('prompt_response_iterations')
        .select('*')
        .eq('score_id', ui.score_id)
        .order('turn_index', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Failed to load response iterations:', error);
        return;
      }

      setResponseIterations((data || []).map((item) => ({
        ...item,
        directions_json: Array.isArray(item.directions_json) ? item.directions_json : [],
        specificity_examples_json: Array.isArray(item.specificity_examples_json) ? item.specificity_examples_json : [],
        analysis_json: item.analysis_json && typeof item.analysis_json === 'object' ? item.analysis_json : {},
      })) as PromptResponseIteration[]);
    };

    loadResponseIterations();
  }, [ui.score_id, responseStepMock]);

  useEffect(() => {
    if (!ui.score_id) return;

    const loadJourneyReceiptData = async () => {
      const [{ data: conversationData }, { data: remixData }] = await Promise.all([
        supabase
          .from("builder_conversations")
          .select("initial_goal, final_prompt, messages_json")
          .eq("score_id", ui.score_id)
          .order("completed_at", { ascending: false, nullsFirst: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("prompt_remixes")
          .select("original_score_id")
          .eq("remixed_score_id", ui.score_id)
          .maybeSingle(),
      ]);

      if (conversationData) {
        setJourneyConversation(conversationData as JourneyConversation);
      }

      const originalScoreId = remixData?.original_score_id || ui.original_score_id;
      if (originalScoreId) {
        setIsRemixReceipt(true);
      }

      if (!originalScoreId || remixOriginalPrompt || ui.source_original_prompt || !membership?.org_id) {
        return;
      }

      const { data: originalDetail } = await supabase.rpc("get_prompt_detail_for_org", {
        _org_id: membership.org_id,
        _score_id: originalScoreId,
      });

      const originalPrompt = originalDetail?.[0]?.best_prompt_extracted || originalDetail?.[0]?.original_content;
      if (originalPrompt) {
        setRemixOriginalPrompt(originalPrompt);
      }
    };

    loadJourneyReceiptData();
  }, [ui.score_id, ui.original_score_id, ui.source_original_prompt, membership?.org_id, remixOriginalPrompt]);

  // Calculate overall score from dimension_scores if available
  // Handle both formats: { key: number } and { key: { score, rationale } }
  const overallScore = useMemo(() => {
    if (ui.dimension_scores && Object.keys(ui.dimension_scores).length > 0) {
      const values = Object.values(ui.dimension_scores);
      let totalScore = 0;
      for (const d of values) {
        const score = typeof d === 'number' ? d : d?.score ?? 0;
        totalScore += score;
      }
      const totalMax = DIMENSIONS.reduce((sum, dim) => sum + dim.max, 0);
      return totalMax > 0 ? Math.round(totalScore / totalMax * 100) : 0;
    }
    // Fallback to old scoring
    return (ui.progress_points?.total_points ?? 0) * 10;
  }, [ui.dimension_scores, ui.progress_points?.total_points]);

  // Remix handler
  const handleRemix = useCallback(() => {
    if (!ui.score_id) return;
    navigate('/build/new', {
      state: {
        remixContext: {
          originalPrompt: currentRewriteClean,
          originalScoreId: ui.score_id,
        },
        returnTo: '/prompt',
      },
    });
  }, [navigate, currentRewriteClean, ui.score_id]);

  const handleRemixPrompt = useCallback((prompt: string) => {
    if (!ui.score_id || !prompt.trim()) return;
    navigate('/build/new', {
      state: {
        remixContext: {
          originalPrompt: prompt,
          originalScoreId: ui.score_id,
        },
        returnTo: '/prompt',
      },
    });
  }, [navigate, ui.score_id]);

  // Original score - from API only, no fabricated fallback
  const originalScore = ui.original_score;
  const improvedScore = ui.improved_score ?? overallScore;

  // Re-score handler - called when user edits the prompt
  const handlePromptChange = useCallback(async (newPrompt: string) => {
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('rescore-prompt', {
        body: {
          prompt: newPrompt
        }
      });
      if (error) {
        console.error('Rescore error:', error);
        toast.error('Failed to re-score prompt');
        return;
      }
      if (data?.score) {
        setCurrentScore(data.score);
      }
    } catch (err) {
      console.error('Rescore failed:', err);
      toast.error('Failed to re-score prompt');
    }
  }, []);

  // Unified handler for prompt updates (from editing or context additions)
  const handlePromptUpdate = useCallback((newClean: string, newTagged?: string) => {
    setCurrentRewriteClean(newClean);
    if (newTagged) setCurrentRewriteTagged(newTagged);
    // Trigger re-score
    handlePromptChange(newClean);
  }, [handlePromptChange]);

  // Tool optimization handler
  const handleOptimizedPrompt = useCallback((toolName: string, _toolModel: string, optimizedPrompt: string) => {
    setSelectedToolName(toolName);
    setCurrentRewriteClean(optimizedPrompt);
    // Use the optimized prompt as-is for tagged (no color highlights for tool-optimized versions)
    setCurrentRewriteTagged(optimizedPrompt);
  }, []);

  // Save title handler
  const handleSaveTitle = useCallback(async () => {
    if (!ui.score_id || editingTitle === promptTitle) {
      setIsEditingTitle(false);
      return;
    }

    setIsSavingTitle(true);
    try {
      // Get current evaluation_markdown and update prompt_title
      const { data: scoreData, error: fetchError } = await supabase
        .from('fluency_scores')
        .select('evaluation_markdown')
        .eq('id', ui.score_id)
        .single();

      if (fetchError) throw fetchError;

      let evalJson: Record<string, unknown> = {};
      if (scoreData?.evaluation_markdown) {
        try {
          evalJson = JSON.parse(scoreData.evaluation_markdown);
        } catch (parseError) {
          console.warn('Failed to parse evaluation markdown while saving title:', parseError);
        }
      }

      evalJson.prompt_title = editingTitle;

      const { error: updateError } = await supabase
        .from('fluency_scores')
        .update({ evaluation_markdown: JSON.stringify(evalJson) })
        .eq('id', ui.score_id);

      if (updateError) throw updateError;

      setPromptTitle(editingTitle);
      toast.success('Title updated');
    } catch (err) {
      console.error('Failed to save title:', err);
      toast.error('Failed to save title');
      setEditingTitle(promptTitle);
    } finally {
      setIsSavingTitle(false);
      setIsEditingTitle(false);
    }
  }, [ui.score_id, editingTitle, promptTitle]);

  const handleCancelEdit = useCallback(() => {
    setEditingTitle(promptTitle);
    setIsEditingTitle(false);
  }, [promptTitle]);

  // Reusable bottom banner for post-onboarding
  const BottomBanner = () => showBottomBanner ? (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/10 p-3 fade-in-up opacity-0 stagger-2">
      <span className="text-sm text-foreground">
        Ready to build your next prompt?
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-primary hover:text-primary/80 gap-1"
          onClick={() => navigate('/prompt')}
        >
          Start building <ArrowRight className="h-3.5 w-3.5" />
        </Button>
        <button
          onClick={() => setShowBottomBanner(false)}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  ) : null;

  // Shared header component
  const HeaderSection = () => <>
      {/* Prompt Details label */}
      <p className="text-base font-medium text-muted-foreground mb-1 fade-in-up opacity-0">Prompt Details</p>
      {/* Page Header with Add Prompt CTA */}
      <div className="flex items-center justify-between gap-3 md:gap-4 fade-in-up opacity-0">
        {/* Left: Icon + Title + Tags */}
        <div className="flex items-center gap-2 md:gap-3 flex-wrap min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center border border-primary/20 flex-shrink-0">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          
          {/* Editable Title */}
          {isEditingTitle ? (
            <div className="flex items-center gap-2">
              <Input
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                className="h-8 text-lg font-bold max-w-[200px]"
                autoFocus
                disabled={isSavingTitle}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleSaveTitle}
                disabled={isSavingTitle}
              >
                <Check className="h-4 w-4 text-green-500" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleCancelEdit}
                disabled={isSavingTitle}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h2 className="font-display font-bold text-xl text-foreground">
                {promptTitle}
              </h2>
              {isOwner && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={() => setIsEditingTitle(true)}
                  title="Edit title"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
          
        </div>
        
        {/* Right: intentionally empty — favorite moved to action bar */}
        <div className="flex items-center gap-2 flex-shrink-0" />
      </div>

    </>;

  const journeyReceipt = useMemo(() => derivePromptJourneyReceipt({
    conversation: journeyConversation,
    isRemix: isRemixReceipt,
    originalPrompt: isRemixReceipt ? (remixOriginalPrompt || ui.source_original_prompt || ui.original_text) : ui.original_text,
    promptSummary: ui.prompt_summary || ui.why_type,
  }), [
    journeyConversation,
    isRemixReceipt,
    remixOriginalPrompt,
    ui.source_original_prompt,
    ui.original_text,
    ui.prompt_summary,
    ui.why_type,
  ]);

  // Shared rewrite section
  const modelPicker = (
    <PromptModelPicker
      recommendations={toolRecommendations}
      recommendationId={recommendationId || null}
      scoreId={ui.score_id}
      originalPrompt={originalPrompt || baseRewriteClean.current}
      selectedTool={selectedToolName || journeyReceipt.selectedTool || null}
      onOptimizedPrompt={handleOptimizedPrompt}
    />
  );

  const RewriteSection = () => <ImprovedVersionCard rewriteTagged={currentRewriteTagged} rewriteClean={currentRewriteClean} improvements={ui.improvements} rewriteSummary={ui.rewrite_summary} scoreId={ui.score_id} onPromptChange={handlePromptUpdate} onCleanChange={setCurrentRewriteClean} onTaggedChange={setCurrentRewriteTagged} onRemix={ui.score_id ? handleRemix : undefined} optimizedForTool={selectedToolName || journeyReceipt.selectedTool} modelPicker={modelPicker} />;

  const ResponseSection = () => (
    <PromptResponsePanel
      scoreId={ui.score_id}
      originalPrompt={currentRewriteClean || ui.rewrite_clean || ui.original_text}
      promptSummary={ui.prompt_summary || ui.why_type}
      toolRecommendations={toolRecommendations}
      recommendationId={recommendationId || null}
      selectedTool={selectedToolName || journeyReceipt.selectedTool || null}
      iterations={responseIterations}
      onIterationsChange={setResponseIterations}
      onRemixPrompt={handleRemixPrompt}
    />
  );

  // Mobile Layout: Single column with reordered blocks per plan
  if (isMobile) {
    return <div className="w-full max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* 1. Title + Summary */}
          <HeaderSection />

          {/* 2. Score Summary */}
          <PromptScoreSummaryBar flow={journeyReceipt.flow} originalScore={originalScore} improvedScore={improvedScore} currentScore={currentScore} dimensionScores={ui.dimension_scores} quickWins={ui.quick_wins} isChat={isChat} />

          {/* 5. Prompt */}
          <RewriteSection />

          {/* 6. Response continuation */}
          <ResponseSection />

          <BottomBanner />
        </div>
      </div>;
  }

  // Desktop Layout: center prompt workspace + right journey rail
  return <div className="w-full max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* Center Canvas - Prompt Workspace */}
        <div className="space-y-6">
          <HeaderSection />

          <PromptScoreSummaryBar flow={journeyReceipt.flow} originalScore={originalScore} improvedScore={improvedScore} currentScore={currentScore} dimensionScores={ui.dimension_scores} quickWins={ui.quick_wins} isChat={isChat} />

          <RewriteSection />
          <ResponseSection />
          <BottomBanner />
        </div>

        {/* Right Rail - Journey Receipt */}
        <div className="sticky top-4 self-start">
          <PromptJourneyRail
            promptTitle={promptTitle}
            promptSummary={ui.prompt_summary || ui.why_type}
            receipt={journeyReceipt}
            iterations={responseIterations}
          />
        </div>
      </div>
    </div>;
};
export default AnalysisResults;
